import { useState, useEffect } from 'react';
import { Settings2 } from 'lucide-react';
import Modal from '../../../components/ui/Modal';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import { usePets } from '../../../contexts/PetsContext';
import { useToast } from '../../../contexts/ToastContext';
import { formatGrams } from '../../../lib/format';

/**
 * Escape hatch: ajuste manual del stock cuando el sistema y la realidad
 * divergen (ej: alguien sirvió de la bolsa directo, se derramó comida, etc).
 *
 * Stage 2: con array de dispensers, el modal permite elegir uno (si hay
 * varios) + el input de mainStock. Si el grupo no tiene dispensers, solo
 * mainStock. Submit hace 1-2 llamadas al service (el service no acepta
 * múltiples dispensers en una sola llamada).
 */
const SyncStockModal = ({ group, isOpen, onClose, onSuccess }) => {
  const { syncPhysicalStock } = usePets();
  const toast = useToast();

  const dispensers = group.dispensers ?? [];
  const hasDispensers = dispensers.length > 0;

  const [selectedDispenserId, setSelectedDispenserId] = useState(
    dispensers[0]?.id ?? null
  );
  const [dispenserGr, setDispenserGr] = useState('');
  const [mainGr, setMainGr] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const selectedDispenser = dispensers.find((d) => d.id === selectedDispenserId) ?? null;

  useEffect(() => {
    if (isOpen) {
      const firstId = dispensers[0]?.id ?? null;
      setSelectedDispenserId(firstId);
      setMainGr(String(group.mainStockGr ?? 0));
      setError(null);
    }
  }, [isOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  // Cuando cambia el dispenser elegido, pre-cargar su stock actual.
  useEffect(() => {
    if (!isOpen || !selectedDispenser) {
      setDispenserGr('');
      return;
    }
    setDispenserGr(String(selectedDispenser.stockGr ?? 0));
  }, [isOpen, selectedDispenserId, selectedDispenser]);

  const dispenserNum = Number(dispenserGr);
  const mainNum = Number(mainGr);

  const dispenserChanged = selectedDispenser
    ? dispenserNum !== (selectedDispenser.stockGr ?? 0)
    : false;
  const mainChanged = mainNum !== (group.mainStockGr ?? 0);
  const hasChanges = dispenserChanged || mainChanged;

  const dispenserError = (() => {
    if (!selectedDispenser) return null;
    if (!dispenserGr) return 'Requerido';
    if (!Number.isInteger(dispenserNum) || dispenserNum < 0) return 'Inválido';
    if (dispenserNum > selectedDispenser.capacityGr)
      return `Máximo ${formatGrams(selectedDispenser.capacityGr)} (capacidad)`;
    return null;
  })();

  const mainError = (() => {
    if (!mainGr) return 'Requerido';
    if (!Number.isInteger(mainNum) || mainNum < 0) return 'Inválido';
    return null;
  })();

  const isValid = !dispenserError && !mainError && hasChanges;

  const handleSubmit = async (e) => {
    e?.preventDefault();
    if (!isValid) return;
    try {
      setSubmitting(true);
      setError(null);

      // 1) Ajustar mainStock (si cambió). Se hace primero para que si falla
      // el dispenser, la bolsa ya quedó actualizada.
      if (mainChanged) {
        await syncPhysicalStock(group.id, { mainStockGr: mainNum });
      }
      // 2) Ajustar dispenser (si cambió y hay uno seleccionado).
      if (dispenserChanged && selectedDispenser) {
        await syncPhysicalStock(group.id, {
          dispenserId: selectedDispenser.id,
          dispenserStockGr: dispenserNum,
        });
      }

      toast.success('Stock ajustado.');
      onSuccess?.();
      onClose();
    } catch (err) {
      setError(err.message || 'No se pudo ajustar el stock.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={() => !submitting && onClose()} title="Ajustar stock">
      <form onSubmit={handleSubmit} className="flex flex-col gap-4">
        <p className="text-sm text-slate-600">
          Usalo cuando el stock real difiere del que muestra la app
          (alguien sirvió de la bolsa directo, se derramó, etc).
        </p>

        {/* Selector de dispenser cuando hay más de uno */}
        {dispensers.length > 1 && (
          <div>
            <label className="mb-1.5 block text-sm font-medium text-slate-700">
              ¿Qué dispenser?
            </label>
            <div className="flex flex-wrap gap-1.5">
              {dispensers.map((d) => (
                <button
                  key={d.id}
                  type="button"
                  onClick={() => setSelectedDispenserId(d.id)}
                  className={[
                    'rounded-full border px-3 py-1.5 text-xs font-medium transition-colors',
                    selectedDispenserId === d.id
                      ? 'border-brand-500 bg-brand-50 text-brand-800'
                      : 'border-slate-200 bg-white text-slate-700 hover:bg-slate-50',
                  ].join(' ')}
                >
                  {d.name}
                </button>
              ))}
            </div>
          </div>
        )}

        {hasDispensers && selectedDispenser && (
          <Input
            label={`${selectedDispenser.name} (cap. ${formatGrams(selectedDispenser.capacityGr)})`}
            type="number"
            inputMode="numeric"
            value={dispenserGr}
            onChange={(e) => setDispenserGr(e.target.value)}
            error={dispenserError}
            min={0}
            max={selectedDispenser.capacityGr}
          />
        )}

        <Input
          label="Bolsa principal"
          type="number"
          inputMode="numeric"
          value={mainGr}
          onChange={(e) => setMainGr(e.target.value)}
          error={mainError}
          min={0}
        />

        {error && (
          <p className="text-sm text-status-red bg-red-50 rounded-lg px-3 py-2">{error}</p>
        )}

        <div className="flex gap-2">
          <Button variant="secondary" fullWidth onClick={onClose} disabled={submitting}>
            Cancelar
          </Button>
          <Button
            type="submit"
            variant="primary"
            fullWidth
            leftIcon={Settings2}
            isLoading={submitting}
            disabled={!isValid || submitting}
          >
            Guardar ajuste
          </Button>
        </div>
      </form>
    </Modal>
  );
};

export default SyncStockModal;
